import { ZuiTableBaseElement, TableElementConnectedEvent } from './base.js';
import { html } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';
import { style } from './zui-table-css.js';
import '@zywave/zui-spinner';
import { updateCustomState } from '@zywave/zui-base/dist/utils/update-custom-state.js';
import type { ZuiTableRowElement } from './zui-table-row.js';
import type { ZuiTableCellElement, ZuiTableSortDirection } from './zui-table-cell.js';
import type { PropertyValues } from 'lit';

type ZuiTableMode = 'default' | 'fixed-sizing';

/**
 * A standardized responsive table component.
 *
 * @element zui-table
 *
 * @slot - Default, unnamed slot; for inserting `<zui-table-topbar>`, `<zui-table-row>`, and `<zui-table-footer>` elements, including a custom no results view, into `<zui-table>`
 * @slot no-results-message - Customize the no results message that is shown when the `no-results` attribute is set on `<zui-table>`: `<zui-table no-results>`
 * @slot footer - Only for `<zui-table-footer>`. When there is a `<zui-table-footer>` present inside `<zui-table>`, it will be auto-assigned to this slot in order to place it outside of the rendered table for styling purposes.
 *
 * @cssprop [--zui-table-cell-padding=13px 20px] - Override cell padding
 * @cssprop [--zui-table-columns-template=repeat(auto-fit, minmax(0, 1fr))] - Override the table columns template. See https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns for more information on valid values.
 * @cssprop [--zui-table-footer-margin=10px] - Override the margin between the table and footer of the table
 * @cssprop [--zui-table-max-height=none] - Set the maximum height of the table when table `mode` is set to `fixed-sizing`. Required for vertical scrolling to work.
 * @cssprop [--zui-table-max-width=none] - Set the maximum width of the table when table `mode` is set to `fixed-sizing`, but not required for horizontal scrolling to work.
 * @cssprop [--zui-table-loading-min-height=20rem] - Override the minimum height of the table body when in a loading state
 * @cssprop [--zui-table-summary-background-color=var(--zui-gray-600)] - Override the table summary background color
 * @cssprop [--zui-table-summary-text-color=#fff] - Override the the table summary text color
 *
 * @event sort - Event fires when the table's active sort changes. Event detail is an object with the `sort` direction and `cell` ZuiTableCellElement.
 *
 * @cssState mobile - Applied when the table is rendered in mobile mode
 */
export class ZuiTableElement extends ZuiTableBaseElement {
  /**
   * Set for alternating table row background colors
   */
  @property({ type: Boolean, reflect: true })
  banded = false;

  /**
   * Ability to modify the table's layout behavior. The supported values are:
   * - `default` (default): Table width is determined by its content. Columns will expand to fit the content within them.
   * - `fixed-sizing`: Opts-in to constrainig dimensions by the `--zui-table-max-width` and `--zui-table-max-height` CSS properties. This unlocks the ability to make a table vertically and horizontally scrollable within a container. The following CSS properties are required: `--zui-table-columns-template`. You should set at least one of the following: `--zui-table-max-width`, `--zui-table-max-height`.
   * @type { 'default' | 'fixed-sizing' }
   */
  @property({ reflect: true })
  mode: ZuiTableMode = 'default';

  // TODO: add divided prop so all table cells will have 1px border

  /**
   * Set to show or hide no results message when there are no results; use in conjunction with the assigned slot `no-results-message` to include a no results message
   */
  @property({ type: Boolean, reflect: true, attribute: 'no-results' })
  noResults = false;

  /**
   * Set to show a loading spinner in the table
   */
  @property({ type: Boolean, reflect: true })
  loading = false;

  @queryAssignedElements({ selector: 'zui-table-row' })
  private _rows: Array<ZuiTableRowElement>;

  #internals: ElementInternals;
  #sortLock = false;

  static get styles() {
    return [super.styles, style];
  }

  constructor() {
    super();

    this.#internals = this.attachInternals();
    this.#internals.role = 'table';

    ZuiTableBaseElement._globalChannel.addEventListener('connected', (event: TableElementConnectedEvent) => {
      if (this.contains(event.detail.element)) {
        this._associateElement(event.detail.element);
      }
    });

    this._state.channel.addEventListener('sort', (event: CustomEvent<SortEventDetail>) => {
      if (this.#sortLock) {
        return;
      }
      this.#sortLock = true;
      this.dispatchEvent(new CustomEvent<SortEventDetail>('sort', { detail: event.detail, bubbles: true }));
      setTimeout(() => (this.#sortLock = false), 0);
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'table');
  }

  firstUpdated(changedProperties: PropertyValues<this>) {
    super.firstUpdated(changedProperties);
    this.#addRowHeadersToCells();
  }

  updated(changedProperties: PropertyValues<this>) {
    super.updated(changedProperties);

    const footer = this.querySelector('zui-table-footer:not([slot])');
    const header = this.querySelector('zui-table-row[header]:not([slot])');
    const topbar = this.querySelector('zui-table-topbar:not([slot])');
    const summary = this.querySelector('zui-table-row[summary]:not([slot])');
    footer?.setAttribute('slot', 'footer');
    header?.setAttribute('slot', 'header');
    topbar?.setAttribute('slot', 'topbar');
    summary?.setAttribute('slot', 'summary');
  }

  render() {
    return html`<div class="wrapper">
        <slot name="topbar"></slot>
        <div class="table-wrapper">
          <div class="table" role="rowgroup">
            <slot name="header" @slotchange="${this.#onSlotChange}"></slot>
            <div class="body">
              <slot @slotchange="${this.#onSlotChange}"></slot>
              ${this.loading ? html`<zui-spinner active="" part="loader"></zui-spinner>` : html``}
              <div class="no-results"><slot name="no-results-message"></slot></div>
            </div>
          </div>
          <slot name="summary"></slot>
        </div>
      </div>
      <div><slot name="footer"></slot></div>`;
  }

  #onSlotChange() {
    this.#addRowHeadersToCells();
    this.requestUpdate();
  }

  async #addRowHeadersToCells() {
    const headerRow = this.querySelector('zui-table-row[header]');

    if (headerRow) {
      const rowBodyList = this._rows.filter((r) => !r.hasAttribute('header') && !r.hasAttribute('summary'));
      for (const row of rowBodyList) {
        for (let i = 0; i < row.children.length; i++) {
          const cell = row.children[i];
          (cell as ZuiTableCellWithState)._header = headerRow.children?.[i]?.textContent ?? '';
        }
      }
    }

    updateCustomState(this.#internals, 'add', 'mobile');
  }
}

window.customElements.define('zui-table', ZuiTableElement);

declare global {
  interface HTMLElementTagNameMap {
    'zui-table': ZuiTableElement;
  }
}

export type SortEventDetail = { sort: ZuiTableSortDirection; cell: ZuiTableCellElement };

type ZuiTableCellWithState = HTMLElement & { _header: string };
